<template>
  <div class="no-tabs notifications">
    <div class="action-panel mb-3">
      <a
        v-if="selectedItems.length > 0"
        class="btn btn-danger"
        @click="confirmDelete"
      >
        {{ $t('components.notificationsHistory.dataGrids.deleteSelected') }}
      </a>
      <a
        v-if="selectedItems.length == 0"
        class="btn btn-warning"
        @click="openDeleteAllModal"
      >
        {{ $t('components.notificationsHistory.dataGrids.deleteAll') }}
      </a>
    </div>
    <NotificationsModal
      :visible="deleteAllModalVisible"
      @close="closeDeleteAllModal"
      @refreshDataGrid="refreshDataGrid"
    />
    <DxDataGrid
      :ref="dataGridRef"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :column-hiding-enabled="true"
      :data-source="customDataSource"
      :hover-state-enabled="true"
      :remote-operations="remoteOperations"
      :show-borders="true"
      :show-column-lines="true"
      @selection-changed="onSelectionChanged"
      class="notifications-history-grid"
    >
      <DxSelection
        v-if="adminOrBoss"
        :show-check-boxes-mode="showCheckBoxesMode"
        mode="multiple"
      />
      <DxHeaderFilter :visible="false" />
      <DxPaging :page-size="pageSize" />
      <DxPager
        :show-info="true"
        :show-navigation-buttons="true"
        :visible="true"
        :allowed-page-sizes="[10, 20, 50, 100]"
        :show-page-size-selector="true"
      />
      <DxFilterRow :visible="false" />
      <DxColumnFixing :enabled="true" />
      <DxSearchPanel
        :visible="true"
        :width="140"
        :placeholder="$t('components.notificationsHistory.dataGrids.search')"
      />

      <!-- Adjusted column widths for better layout -->
      <DxColumn
        :allow-filtering="true"
        :allow-header-filtering="false"
        caption="#"
        :filter-operations="allowedOperations"
        data-field="notificationHistory.id"
        :width="columnWidths.id"
      />
      <DxColumn
        :allow-filtering="true"
        :caption="$t('components.notificationsHistory.dataGrids.email')"
        data-field="notificationHistory.email"
        :width="columnWidths.email"
      />
      <DxColumn
        :allow-filtering="true"
        :caption="$t('components.notificationsHistory.dataGrids.vehicle')"
        data-field="full_vehicle_details"
        cell-template="vehicle-cell-template"
        :width="columnWidths.vehicle"
      />
      <DxColumn
        :allow-filtering="true"
        :caption="$t('components.notificationsHistory.dataGrids.isSentEmail')"
        data-field="notificationHistory.is_sent_by_email"
        :width="columnWidths.isSentEmail"
      />
      <DxColumn
        :allow-filtering="true"
        :caption="$t('components.notificationsHistory.dataGrids.isSentSms')"
        data-field="notificationHistory.is_sent_by_sms"
        :width="columnWidths.isSentSms"
      />
      <DxColumn
        :allow-filtering="true"
        :caption="$t('components.notificationsHistory.dataGrids.message')"
        data-field="notificationHistory.message"
        :cell-template="renderTranslatedMessage"
        :width="columnWidths.message"
      />
      <DxColumn
        :allow-filtering="true"
        :caption="$t('components.notificationsHistory.dataGrids.sentAt')"
        data-field="notificationHistory.sent_at"
        :cell-template="sentAtTemplate"
        :width="columnWidths.sentAt"
      />

      <template #vehicle-cell-template="{ data }">
        <p class="fw-bold fs-6 mb-1 vehicle-name">
          {{ data.data.vehicle?.vars?.name || data.data.vehicle?.registration || 'N/A' }}
        </p>
        <p class="fs-6 mb-1 vehicle-imei">
          <b>IMEI:</b> {{ data.data.notificationHistory?.data_last_audit?.data_last?.imei || 'N/A' }}
        </p>
      </template>
    </DxDataGrid>
  </div>
</template>


<script lang="ts">
import { defineComponent } from 'vue';
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxSelection,
} from 'devextreme-vue/data-grid';
import { getNotificationsHistoryList } from '@/crud/notifcations_history/getters';
import { checkIfAdminOrBoss } from '@/utils/tools';
import { getHumanDateTimeFromDateTime } from '@/utils/tools';
import { massRemoveNotificationsHistory } from '@/crud/removers';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import eventBus from '@/core/plugins/eventBus';
import NotificationsModal from "@/components/content/_modals/NotificationsModal.vue";

export default defineComponent({
  name: 'NotificationsHistory',
  components: {
    NotificationsModal,
    DxDataGrid,
    DxHeaderFilter,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxColumnFixing,
    DxColumn,
    DxSearchPanel,
    DxSelection,
  },
  data() {
    return {
      dataGridRef: 'sentNotificationsHistoryGrid',
      allowedOperations: ['contains', '=', 'notcontains', '!='],
      remoteOperations: { filtering: true, sorting: true, paging: true },
      selectedItems: [],
      showCheckBoxesMode: 'always',
      adminOrBoss: false,
      deleteAllModalVisible: false,
      windowWidth: window.innerWidth,
      customDataSource: {
        load: (loadOptions: any) => {
          return getNotificationsHistoryList(loadOptions)
              .then((result) => {
                const data = result.data.map((obj: any) => {
                  const imei = obj.notificationHistory?.data_last_audit?.data_last?.imei ||
                      obj.notificationHistory?.geo_fencing_audit?.geofencing_event?.imei;
                  const fullVehicleDetails = `${
                      obj.vehicle?.vars?.name || obj.vehicle?.registration || 'N/A'
                  } ${imei || ''} ${obj.vehicle?.vin || ''}`;

                  return {
                    ...obj,
                    full_vehicle_details: fullVehicleDetails,
                  };
                });
                return {
                  data: data,
                  totalCount: result.totalCount,
                };
              })
              .catch(() => {
                this.$toast.error(
                    this.t('components.notificationsHistory.dataGrids.errorWhileLoading')
                );
                return {
                  data: [],
                  totalCount: 0,
                };
              });
        },
      },
    };
  },
  computed: {
    dataGrid() {
      return (this.$refs[this.dataGridRef] as any).instance;
    },
    pageSize() {
      return this.windowWidth <= 768 ? 10 : 20;
    },
    isMobile() {
      return this.windowWidth <= 768;
    },
    columnWidths() {
      if (this.isMobile) {
        return {
          id: 50,
          email: 120,
          vehicle: 100,
          isSentEmail: 60,
          isSentSms: 60,
          message: 150,
          sentAt: 80
        };
      } else {
        return {
          id: 100,
          email: 300,
          vehicle: 180,
          isSentEmail: 120,
          isSentSms: 120,
          message: 300,
          sentAt: 150
        };
      }
    },
  },
  setup() {
    const { t } = useI18n();

    return {
      quasar: useQuasar(),
      t,
    };
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    openDeleteAllModal() {
      this.deleteAllModalVisible = true;
    },
    closeDeleteAllModal() {
      this.deleteAllModalVisible = false;
    },
    renderTranslatedMessage(cellElement, cellInfo) {
      const notif = cellInfo.data.notificationHistory;
      const message = notif?.message
          ? JSON.parse(notif.message)
          : { translateKey: null, translateValues: {} };
      const translationKey = message.translateKey
          ? `components.notificationsHistory.messages.${message.translateKey}`
          : null;
      const translationValues = message.translateValues || {};
      cellElement.innerHTML = translationKey
          ? this.t(translationKey, translationValues)
          : notif.message;
    },
    sentAtTemplate(cellElement, cellInfo) {
      cellElement.textContent = this.getHumanDateTimeFromDateTime(
          cellInfo.data.notificationHistory.sent_at
      );
    },
    onSelectionChanged(e) {
      this.selectedItems = e.component.getSelectedRowsData();
    },
    confirmDelete() {
      if (this.selectedItems.length > 0) {
        this.quasar
            .dialog({
              title: this.t(
                  'components.notificationsHistory.dataGrids.deleteConfirmation'
              ),
              message: this.t(
                  'components.notificationsHistory.dataGrids.areYouSureDel',
                  { count: this.selectedItems.length }
              ),
              cancel: true,
            })
            .onOk(() => {
              massRemoveNotificationsHistory(this.selectedItems)
                  .then(() => {
                    this.refreshDataGrid();
                    this.$toast.success(
                        this.t(
                            'components.notificationsHistory.dataGrids.selectedDeleted'
                        )
                    );
                    eventBus.emit('notificationsRemoved');
                  })
                  .catch(() => {
                    this.$toast.error(
                        this.t(
                            'components.notificationsHistory.dataGrids.errorWhileDeleting'
                        )
                    );
                  });
            });
      }
    },
    refreshDataGrid() {
      if (this.dataGrid) {
        this.dataGrid.refresh();
      }
    },
    getHumanDateTimeFromDateTime: getHumanDateTimeFromDateTime,
  },
  mounted() {
    eventBus.on('newNotificationReceived', this.refreshDataGrid);
    this.adminOrBoss = checkIfAdminOrBoss();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    eventBus.off('newNotificationReceived', this.refreshDataGrid);
    window.removeEventListener('resize', this.handleResize);
  },
});
</script>

<style lang="scss" scoped>
.reduced-margin-bottom {
  margin-bottom: 5px;
}

.rounded-pill {
  width: 100%;
  text-transform: uppercase;
}

.fw-bold {
  font-weight: 700 !important;
}

.notifications {
  padding: 15px;

  @media (max-width: 768px) {
    padding: 8px;
  }

  .action-panel {
    @media (max-width: 768px) {
      margin-bottom: 8px !important;

      .btn {
        font-size: 12px;
        padding: 6px 12px;
      }
    }
  }

  .notifications-history-grid {
    max-width: 100% !important;

    @media (max-width: 768px) {
      font-size: 12px;

      :deep(.dx-datagrid) {
        .dx-row {
          min-height: 35px;
        }

        .dx-header-row {
          min-height: 30px;
          font-size: 11px;
        }

        .dx-datagrid-headers {
          font-size: 10px;
          font-weight: 600;
        }

        .dx-data-row {
          .dx-command-select {
            width: 35px;
          }
        }

        .dx-texteditor {
          font-size: 11px;
        }

        .dx-toolbar {
          min-height: 35px;

          .dx-toolbar-item {
            margin: 2px;
          }

          .dx-button {
            min-height: 30px;
            font-size: 11px;
          }

          .dx-textbox {
            font-size: 11px;
          }
        }

        .dx-pager {
          .dx-page {
            min-width: 30px;
            min-height: 30px;
            font-size: 11px;
          }

          .dx-pager-info {
            font-size: 10px;
          }

          .dx-selectbox {
            font-size: 11px;
          }
        }
      }

      .vehicle-name {
        font-size: 11px !important;
        margin-bottom: 2px !important;
        line-height: 1.2;
      }

      .vehicle-imei {
        font-size: 9px !important;
        margin-bottom: 2px !important;
        line-height: 1.1;
      }
    }

    .dx-freespace-row {
      height: 100%;
    }
  }
}

.btn-warning {
  background-color: #252422 !important;
  border-color: #252422 !important;
}

.btn-warning:hover,
.btn-warning:focus,
.btn-warning:active,
.btn-warning.active {
  background-color: #e66f00 !important;
  border-color: #e66f00 !important;
}
</style>